'use client';

import { useState } from 'react';
import toast from 'react-hot-toast';
import { Body, Heading } from '../typography';
import { Button } from './Button';
import { FlexContainer } from './FlexContainer';
import SheetNavigationHeader from './SheetNavigationHeader';
import SheetWrapper from './SheetWrapper';
import TaskChips from './TaskChips';
import type { SheetWrapperProps } from './SheetWrapper';
import type { TaskChip } from './TaskChips';
export interface CustomerTaskSheetProps {
  children: React.ReactNode;
  chips?: TaskChip[];
  currentStepIndex: number;
  currentStepTitle?: string;
  description?: string;
  direction?: 'next' | 'previous';
  formId?: string;
  hasNextStep: boolean;
  hasPreviousStep: boolean;
  isPending?: boolean;
  mode?: 'interactive' | 'readOnly';
  onNavigateBack?: () => void;
  onOpenChange: (open: boolean) => void;
  onPrimaryAction?: () => void;
  onSaveProgress?: () => Promise<void>;
  onSkipStep?: () => void;
  open: boolean;
  previewNotice?: string;
  size?: SheetWrapperProps['size'];
  stepCount: number;
  stepKey?: string;
  title?: string;
}

/** Shared customer-task sheet chrome and step navigation. Apps own task side effects. */
export function CustomerTaskSheet({
  children,
  chips,
  currentStepIndex,
  currentStepTitle,
  description,
  direction,
  formId,
  hasNextStep,
  hasPreviousStep,
  isPending,
  mode = 'interactive',
  onNavigateBack,
  onOpenChange,
  onPrimaryAction,
  onSaveProgress,
  onSkipStep,
  open,
  previewNotice,
  size,
  stepCount,
  stepKey,
  title
}: CustomerTaskSheetProps) {
  const [isSaving, setIsSaving] = useState(false);
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveProgress?.();
      toast.success('Progress saved');
    } catch {
      // error toast handled upstream
    } finally {
      setIsSaving(false);
    }
  };
  return <SheetWrapper confirmBeforeClose={mode === 'interactive' && currentStepIndex > 0} contentKey={stepKey} direction={direction} footer={<FlexContainer className="ml-auto gap-4" direction="row">
          {hasNextStep && onSkipStep && <Button onClick={onSkipStep} variant="quaternary">
              Skip
            </Button>}
          {onSaveProgress && <Button onClick={() => void handleSave()} pending={isSaving} variant="secondary">
              Save progress
            </Button>}
          <Button form={mode === 'interactive' ? formId : undefined} onClick={mode === 'readOnly' ? onPrimaryAction : undefined} pending={isPending} type={mode === 'interactive' ? 'submit' : 'button'}>
            {hasNextStep ? 'Next' : mode === 'interactive' ? 'Complete' : 'Done'}
          </Button>
        </FlexContainer>} header={<SheetNavigationHeader description={description} onNavigateBack={hasPreviousStep ? onNavigateBack : undefined} title={title ?? 'Complete task'}>
          <Body className="font-mono text-content-secondary uppercase" size="sm">
            Step {currentStepIndex + 1}/{stepCount}
          </Body>
        </SheetNavigationHeader>} onOpenChange={onOpenChange} open={open} size={size}>
      <FlexContainer className="gap-8" direction="column">
        {previewNotice && <Body className="block text-content-secondary" size="sm">
            {previewNotice}
          </Body>}
        {currentStepTitle && <Heading size="sm" weight="medium">
            {currentStepTitle}
          </Heading>}
        <TaskChips chips={chips} />
        {children}
      </FlexContainer>
    </SheetWrapper>;
}